import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded';
import SmsRoundedIcon from '@mui/icons-material/SmsRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import DirectionsBikeOutlinedIcon from '@mui/icons-material/DirectionsBikeOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

// ── Palette per action type ───────────────────────────────────────────────────
const TYPE_CONFIG = {
  call: {
    accent: '#16A34A',
    accentDark: '#15803D',
    accentLight: '#DCFCE7',
    accentBorder: '#86EFAC',
    gradientFrom: '#166534',
    gradientTo: '#15803D',
    Icon: PhoneInTalkRoundedIcon,
    label: 'Voice call',
  },
  sms: {
    accent: '#2563EB',
    accentDark: '#1D4ED8',
    accentLight: '#DBEAFE',
    accentBorder: '#93C5FD',
    gradientFrom: '#1E40AF',
    gradientTo: '#2563EB',
    Icon: SmsRoundedIcon,
    label: 'Send SMS',
  },
  push: {
    accent: '#EA580C',
    accentDark: '#C2410C',
    accentLight: '#FFF7ED',
    accentBorder: '#FDBA74',
    gradientFrom: '#9A3412',
    gradientTo: '#EA580C',
    Icon: NotificationsActiveRoundedIcon,
    label: 'Push notification',
  },
};

const ROLE_ICON = {
  Restaurant: StorefrontOutlinedIcon,
  Customer: PersonOutlinedIcon,
  Driver: DirectionsBikeOutlinedIcon,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function getInitials(name) {
  if (!name || name === '—') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function AvatarCircle({ name, role, size = 64, fontSize = 22 }) {
  const RoleIcon = ROLE_ICON[role] || PersonOutlinedIcon;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
        border: '2.5px solid rgba(255,255,255,0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
      }}
    >
      {name && name !== '—' ? (
        <Typography
          sx={{
            fontFamily: 'Inter Bold, sans-serif',
            fontWeight: 800,
            fontSize,
            color: '#fff',
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          {getInitials(name)}
        </Typography>
      ) : (
        <RoleIcon sx={{ fontSize: fontSize + 2, color: 'rgba(255,255,255,0.7)' }} />
      )}
    </Box>
  );
}

// Pulsing ring used around the call button
const pulseKeyframes = {
  '@keyframes pulse_ring': {
    '0%': { transform: 'scale(1)', opacity: 0.55 },
    '70%': { transform: 'scale(1.55)', opacity: 0 },
    '100%': { transform: 'scale(1.55)', opacity: 0 },
  },
};

function PulseRing({ color, size = 64 }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        borderRadius: '50%',
        border: `2px solid ${color}`,
        animation: 'pulse_ring 1.6s ease-out infinite',
        ...pulseKeyframes,
      }}
    />
  );
}

// ── Shared header / close ─────────────────────────────────────────────────────
function ModalHeader({ type, onClose, children }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.call;
  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${cfg.gradientFrom} 0%, ${cfg.gradientTo} 100%)`,
        px: 2.5,
        pt: 2.5,
        pb: 2,
        position: 'relative',
        borderRadius: '18px 18px 0 0',
      }}
    >
      <IconButton
        onClick={onClose}
        size='small'
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: 'rgba(255,255,255,0.7)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 18 }} />
      </IconButton>
      {children}
    </Box>
  );
}

// ── Contact info card (inside modal body) ─────────────────────────────────────
function ContactCard({ contact, phoneMissing }) {
  if (!contact) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        p: 1.75,
        borderRadius: '12px',
        bgcolor: '#F9FAFB',
        border: '1px solid #E5E7EB',
      }}
    >
      <AvatarCircle name={contact.name} role={contact.role} size={48} fontSize={18} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 14, color: '#111827', lineHeight: 1.3, mb: 0.3 }}
        >
          {contact.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip
            label={contact.role}
            size='small'
            sx={{
              height: 18,
              fontSize: 10.5,
              fontFamily: 'Inter Bold, sans-serif',
              fontWeight: 700,
              bgcolor: '#E5E7EB',
              color: '#374151',
              borderRadius: '6px',
              '& .MuiChip-label': { px: 0.85, py: 0 },
            }}
          />
          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11.5, color: '#9CA3AF' }}>
            Order #{contact.orderNo ?? '—'}
          </Typography>
        </Box>
        {contact.phone ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
            <PhoneOutlinedIcon sx={{ fontSize: 12, color: '#6B7280' }} />
            <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#374151' }}>
              {contact.phone}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#EF4444', mt: 0.5 }}>
            No phone on file
          </Typography>
        )}
        {contact.address && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.35 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 12, color: '#9CA3AF', mt: 0.15, flexShrink: 0 }} />
            <Typography
              sx={{
                fontFamily: 'Inter Regular, sans-serif',
                fontSize: 11.5,
                color: '#9CA3AF',
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {contact.address}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── Success state ─────────────────────────────────────────────────────────────
function SuccessView({ type }) {
  const msgs = { call: 'Call initiated', sms: 'SMS sent successfully', push: 'Notification delivered' };
  return (
    <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ position: 'relative' }}>
        <CheckCircleRoundedIcon sx={{ fontSize: 56, color: '#16A34A' }} />
      </Box>
      <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 16, color: '#111827' }}>
        {msgs[type] ?? 'Done'}
      </Typography>
      <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 13, color: '#6B7280', textAlign: 'center', maxWidth: 260 }}>
        {type === 'call' && 'Your device dialer should open shortly.'}
        {type === 'sms' && 'The message has been queued for delivery.'}
        {type === 'push' && 'The recipient will see the notification on their device.'}
      </Typography>
    </Box>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
/** @param {{ open: boolean, type: 'call'|'sms'|'push', contact: object|null, onClose: function, onSubmit: function }} */
export function ContactActionModal({ open, type, contact, onClose, onSubmit }) {
  const [smsMessage, setSmsMessage] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setSmsMessage('');
      setPushTitle('');
      setPushMessage('');
      setSubmitting(false);
      setDone(false);
      return;
    }
    if (type === 'push' && contact) {
      setPushTitle(`Order update — #${contact.orderNo ?? ''}`);
      setPushMessage('');
    }
    if (type === 'sms') setSmsMessage('');
  }, [open, type, contact]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload =
        type === 'sms'
          ? { type, contact, message: smsMessage.trim() }
          : type === 'push'
            ? { type, contact, title: pushTitle.trim(), message: pushMessage.trim() }
            : { type, contact };
      await onSubmit?.(payload);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  const phoneMissing = !contact?.phone;
  const smsValid = smsMessage.trim().length > 0;
  const pushValid = pushTitle.trim().length > 0 && pushMessage.trim().length > 0;
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.call;

  const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontFamily: 'Inter Regular, sans-serif',
      fontSize: 13.5,
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: cfg.accent },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: cfg.accent },
    '& .MuiInputLabel-root': { fontFamily: 'Inter Regular, sans-serif', fontSize: 13 },
  };

  // ── CALL ────────────────────────────────────────────────────────────────────
  if (type === 'call') {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='xs'
        PaperProps={{
          sx: {
            borderRadius: '18px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
            border: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      >
        {/* Gradient header */}
        <ModalHeader type='call' onClose={handleClose}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pb: 1.5, pt: 0.5 }}>
            {/* Avatar with pulse ring */}
            <Box sx={{ position: 'relative', width: 72, height: 72 }}>
              {!done && <PulseRing color='rgba(255,255,255,0.5)' size={72} />}
              <AvatarCircle name={contact?.name} role={contact?.role} size={72} fontSize={26} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 800, fontSize: 17, color: '#fff', lineHeight: 1.25 }}>
                {done ? 'Call initiated' : (contact?.name ?? '—')}
              </Typography>
              {!done && (
                <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: 'rgba(255,255,255,0.7)', mt: 0.4 }}>
                  {contact?.role ?? ''}{contact?.orderNo ? ` · Order #${contact.orderNo}` : ''}
                </Typography>
              )}
            </Box>
          </Box>
        </ModalHeader>

        {/* Body */}
        <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#fff' }}>
          {done ? (
            <SuccessView type='call' />
          ) : (
            <>
              {/* Phone number display */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 1.5,
                  borderRadius: '12px',
                  bgcolor: phoneMissing ? '#FEF2F2' : '#F0FDF4',
                  border: `1.5px solid ${phoneMissing ? '#FECACA' : '#86EFAC'}`,
                }}
              >
                <PhoneOutlinedIcon sx={{ fontSize: 15, color: phoneMissing ? '#DC2626' : '#16A34A' }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter Bold, sans-serif',
                    fontWeight: 700,
                    fontSize: 15,
                    color: phoneMissing ? '#DC2626' : '#166534',
                    letterSpacing: 0.5,
                  }}
                >
                  {phoneMissing ? 'No phone number on file' : contact.phone}
                </Typography>
              </Box>

              {/* Info */}
              {contact?.address && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, px: 0.5 }}>
                  <LocationOnOutlinedIcon sx={{ fontSize: 13, color: '#9CA3AF', mt: 0.2, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontFamily: 'Inter Regular, sans-serif',
                      fontSize: 12,
                      color: '#9CA3AF',
                      lineHeight: 1.45,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {contact.address}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Action row */}
        {!done && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              pb: 3,
              pt: 0.5,
              bgcolor: '#fff',
            }}
          >
            {/* Cancel / end call */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
              <Box
                component='button'
                type='button'
                onClick={handleClose}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#F3F4F6',
                  border: '1.5px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  font: 'inherit',
                  '&:hover': { bgcolor: '#E5E7EB' },
                }}
              >
                <CallEndRoundedIcon sx={{ fontSize: 24, color: '#6B7280' }} />
              </Box>
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 11, color: '#9CA3AF' }}>Cancel</Typography>
            </Box>

            {/* Call button */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
              <Box
                component='button'
                type='button'
                onClick={handleSubmit}
                disabled={phoneMissing || submitting}
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: phoneMissing ? '#E5E7EB' : 'linear-gradient(135deg, #166534, #16A34A)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: phoneMissing ? 'not-allowed' : 'pointer',
                  boxShadow: phoneMissing ? 'none' : '0 6px 20px rgba(22,101,52,0.4)',
                  transition: 'all 0.2s',
                  font: 'inherit',
                  '&:hover:not(:disabled)': { transform: 'scale(1.06)', boxShadow: '0 8px 24px rgba(22,101,52,0.5)' },
                  '&:active:not(:disabled)': { transform: 'scale(0.97)' },
                }}
              >
                <PhoneInTalkRoundedIcon sx={{ fontSize: 28, color: phoneMissing ? '#9CA3AF' : '#fff' }} />
              </Box>
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 11, color: phoneMissing ? '#9CA3AF' : '#166534' }}>
                {submitting ? 'Opening…' : 'Call'}
              </Typography>
            </Box>
          </Box>
        )}

        {done && (
          <Box sx={{ pb: 2.5, px: 2.5, bgcolor: '#fff' }}>
            <Button
              fullWidth
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
                fontSize: 14,
                borderRadius: '10px',
                py: 1.1,
                bgcolor: '#F3F4F6',
                color: '#374151',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </Dialog>
    );
  }

  // ── SMS ─────────────────────────────────────────────────────────────────────
  if (type === 'sms') {
    const charCount = smsMessage.length;
    const charMax = 160;
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='sm'
        PaperProps={{
          sx: {
            borderRadius: '18px',
            overflow: 'hidden',
            boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
          },
        }}
      >
        <ModalHeader type='sms' onClose={handleClose}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SmsRoundedIcon sx={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 800, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>
                Send SMS
              </Typography>
              <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
                Text message to {contact?.role?.toLowerCase() ?? 'contact'}
              </Typography>
            </Box>
          </Box>
        </ModalHeader>

        <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#fff' }}>
          {done ? (
            <SuccessView type='sms' />
          ) : (
            <>
              <ContactCard contact={contact} phoneMissing={phoneMissing} />

              <Divider sx={{ borderColor: '#F0F0F0' }} />

              {/* Message composer */}
              <Box
                sx={{
                  borderRadius: '12px',
                  border: `1.5px solid ${smsValid ? cfg.accent : '#E5E7EB'}`,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                }}
              >
                {/* Bubble header */}
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: '#F9FAFB',
                    borderBottom: '1px solid #F0F0F0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <SmsRoundedIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                  <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#9CA3AF' }}>
                    Message
                  </Typography>
                </Box>
                <TextField
                  inputRef={inputRef}
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  multiline
                  minRows={4}
                  maxRows={8}
                  fullWidth
                  placeholder='Type your message here…'
                  variant='standard'
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    '& .MuiInputBase-root': { px: 2, pt: 1.5, pb: 1, fontFamily: 'Inter Regular, sans-serif', fontSize: 13.5, color: '#111827' },
                    '& textarea::placeholder': { color: '#D1D5DB' },
                  }}
                />
                {/* Character count */}
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: '#F9FAFB',
                    borderTop: '1px solid #F0F0F0',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter Regular, sans-serif',
                      fontSize: 11,
                      color: charCount > charMax ? '#EF4444' : '#9CA3AF',
                    }}
                  >
                    {charCount}/{charMax}
                    {charCount > charMax && ' · Will split into multiple SMS'}
                  </Typography>
                </Box>
              </Box>

              {phoneMissing && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    p: 1.5,
                    borderRadius: '10px',
                    bgcolor: '#FEF2F2',
                    border: '1px solid #FECACA',
                  }}
                >
                  <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12.5, color: '#DC2626' }}>
                    No phone number on file — SMS cannot be sent.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.25,
            px: 2.5,
            pb: 2.5,
            pt: 0.5,
            bgcolor: '#fff',
            borderTop: done ? 'none' : '1px solid #F5F5F5',
          }}
        >
          {done ? (
            <Button
              fullWidth
              onClick={handleClose}
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
                fontSize: 14,
                borderRadius: '10px',
                py: 1.1,
                bgcolor: '#F3F4F6',
                color: '#374151',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={handleClose}
                disabled={submitting}
                sx={{
                  textTransform: 'none',
                  color: '#6B7280',
                  fontFamily: 'Inter Regular, sans-serif',
                  borderRadius: '10px',
                  px: 2.5,
                  border: '1.5px solid #E5E7EB',
                  '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                fullWidth
                disabled={!smsValid || phoneMissing || submitting}
                onClick={handleSubmit}
                endIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Inter Bold, sans-serif',
                  fontWeight: 700,
                  fontSize: 14,
                  borderRadius: '10px',
                  py: 1.1,
                  background: (!smsValid || phoneMissing || submitting)
                    ? undefined
                    : `linear-gradient(135deg, ${cfg.accentDark}, ${cfg.accent})`,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: `0 4px 14px rgba(37,99,235,0.35)` },
                  '&.Mui-disabled': { bgcolor: '#F3F4F6', color: '#9CA3AF' },
                }}
              >
                {submitting ? 'Sending…' : 'Send SMS'}
              </Button>
            </>
          )}
        </Box>
      </Dialog>
    );
  }

  // ── PUSH ────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{
        sx: {
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
        },
      }}
    >
      <ModalHeader type='push' onClose={handleClose}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '14px',
              bgcolor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <NotificationsActiveRoundedIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 800, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>
              Push Notification
            </Typography>
            <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
              Notify {contact?.role?.toLowerCase() ?? 'contact'} on their device
            </Typography>
          </Box>
        </Box>
      </ModalHeader>

      <DialogContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#fff' }}>
        {done ? (
          <SuccessView type='push' />
        ) : (
          <>
            <ContactCard contact={contact} phoneMissing={false} />

            <Divider sx={{ borderColor: '#F0F0F0' }} />

            {/* Notification preview */}
            {(pushTitle || pushMessage) && (
              <Box
                sx={{
                  borderRadius: '14px',
                  overflow: 'hidden',
                  border: '1.5px solid #FED7AA',
                  bgcolor: '#FFF7ED',
                }}
              >
                <Box sx={{ px: 2, py: 1, bgcolor: '#FFEDD5', borderBottom: '1px solid #FED7AA', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <NotificationsActiveRoundedIcon sx={{ fontSize: 13, color: '#EA580C' }} />
                  <Typography sx={{ fontFamily: 'Inter Bold, sans-serif', fontWeight: 700, fontSize: 11, color: '#C2410C', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Preview
                  </Typography>
                </Box>
                <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #9A3412, #EA580C)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <NotificationsActiveRoundedIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter Bold, sans-serif',
                        fontWeight: 700,
                        fontSize: 13,
                        color: '#1F2937',
                        lineHeight: 1.35,
                        mb: 0.25,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {pushTitle || 'Notification title'}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Inter Regular, sans-serif',
                        fontSize: 12.5,
                        color: '#6B7280',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {pushMessage || 'Notification body text…'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 10.5, color: '#9CA3AF', flexShrink: 0, mt: 0.25 }}>
                    now
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Fields */}
            <TextField
              label='Title'
              placeholder='e.g. Your order is ready'
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              fullWidth
              required
              sx={FIELD_SX}
            />
            <TextField
              label='Message'
              placeholder='e.g. Please head to the pickup counter'
              value={pushMessage}
              onChange={(e) => setPushMessage(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              required
              sx={FIELD_SX}
            />
          </>
        )}
      </DialogContent>

      <Box
        sx={{
          display: 'flex',
          gap: 1.25,
          px: 2.5,
          pb: 2.5,
          pt: 0.5,
          bgcolor: '#fff',
          borderTop: done ? 'none' : '1px solid #F5F5F5',
        }}
      >
        {done ? (
          <Button
            fullWidth
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontFamily: 'Inter Bold, sans-serif',
              fontWeight: 700,
              fontSize: 14,
              borderRadius: '10px',
              py: 1.1,
              bgcolor: '#F3F4F6',
              color: '#374151',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#E5E7EB', boxShadow: 'none' },
            }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={submitting}
              sx={{
                textTransform: 'none',
                color: '#6B7280',
                fontFamily: 'Inter Regular, sans-serif',
                borderRadius: '10px',
                px: 2.5,
                border: '1.5px solid #E5E7EB',
                '&:hover': { bgcolor: '#F9FAFB', borderColor: '#D1D5DB' },
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              fullWidth
              disabled={!pushValid || submitting}
              onClick={handleSubmit}
              endIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontFamily: 'Inter Bold, sans-serif',
                fontWeight: 700,
                fontSize: 14,
                borderRadius: '10px',
                py: 1.1,
                background: (!pushValid || submitting)
                  ? undefined
                  : `linear-gradient(135deg, ${cfg.accentDark}, ${cfg.accent})`,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 4px 14px rgba(234,88,12,0.35)' },
                '&.Mui-disabled': { bgcolor: '#F3F4F6', color: '#9CA3AF' },
              }}
            >
              {submitting ? 'Sending…' : 'Send notification'}
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
}

// ── Build contact object ──────────────────────────────────────────────────────
export function buildOrderContact(order, who, driver) {
  const orderNo = order?.order_no ?? '—';
  if (who === 'kitchen') {
    return {
      who: 'kitchen',
      role: 'Restaurant',
      name: order?.kitchen_name ?? order?.kitchen_managingBranchName ?? '—',
      phone: order?.kitchen_phone_no ?? null,
      address: order?.kitchen_geolocation?.formattedAddress ?? order?.pickup_kitchen_address ?? null,
      orderNo,
    };
  }
  if (who === 'customer') {
    return {
      who: 'customer',
      role: 'Customer',
      name: `${order?.cust_firstName ?? ''} ${order?.cust_lastName ?? ''}`.trim() || '—',
      phone: order?.customer_phone_no ?? null,
      address: order?.customer_geolocation?.formattedAddress ?? order?.delivery_address ?? null,
      orderNo,
    };
  }
  return {
    who: 'driver',
    role: 'Driver',
    name: `${driver?.first_name ?? ''} ${driver?.last_name ?? ''}`.trim() || '—',
    phone: driver?.phone_no ?? driver?.phone ?? null,
    address: null,
    orderNo,
  };
}
